import { create } from 'zustand';
import type { BibleBookmark } from '@/types/bookmark';
import * as db from '@/lib/db';
import {
  queueBookmarkMutation,
  queueBookmarkNoteMutation,
  requestBookmarkLifecycleSync,
} from '@/lib/sync-bookmarks';
import { assertCoreWriteAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { bookmarkFromCompleteDocument } from '@/lib/content-lifecycle/contracts';
import {
  getOwnerContentEntities,
  hasContentLifecycleEntityBootstrap,
} from '@/lib/content-lifecycle/store';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import type { CompleteBookmarkDocument } from '@/types/content-lifecycle';
import {
  ContentHydrationStaleError,
  recordContentHydrationJoinedLoad,
  runContentHydrationPublishProbe,
} from '@/lib/content-hydration';
import { BookmarkNotFoundError } from '@/lib/bookmark-errors';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';

// In-memory store for Bible bookmarks, mirroring src/lib/store.ts. `bookmarks`
// holds only LIVE rows (tombstones filtered) so every consumer (reader markers,
// list page) can render it directly. Delete is a soft tombstone that rides the
// outbox (see removeBookmark); confirmed-synced tombstones are purged from IDB
// on loadBookmarks. Silent sync, no UI sync state, same posture as prayers.

interface BookmarkState {
  bookmarks: BibleBookmark[];
  isLoading: boolean;

  loadBookmarks: () => Promise<void>;
  addBookmark: (
    bookmark: BibleBookmark,
    recoveredAudio?: readonly RecoveredAudioCommitClaim[],
  ) => Promise<void>;
  updateBookmark: (
    id: string,
    note: string,
    expectedUpdatedAt: string,
    recoveredAudio?: readonly RecoveredAudioCommitClaim[],
  ) => Promise<void>;
  removeBookmark: (id: string) => Promise<void>;
}

let bookmarkLoadRequestId = 0;
let bookmarkLoadInFlight: { key: string; promise: Promise<void> } | null = null;
let bookmarkMutationVersion = 0;
const bookmarkMutationVersions = new Map<string, number>();

function markBookmarkMutation(id: string): void {
  bookmarkMutationVersion += 1;
  bookmarkMutationVersions.set(id, bookmarkMutationVersion);
}

/** Probe-only witness for a durable mutation performed behind the store API. */
export function __recordBookmarkMutationForProbe(id: string): void {
  markBookmarkMutation(id);
}

function mergeBookmarkMutations(
  loaded: BibleBookmark[],
  current: BibleBookmark[],
  startedAtMutationVersion: number,
): BibleBookmark[] {
  const changed = new Set(
    [...bookmarkMutationVersions]
      .filter(([, version]) => version > startedAtMutationVersion)
      .map(([id]) => id),
  );
  if (changed.size === 0) return loaded;
  return [
    ...loaded.filter((bookmark) => !changed.has(bookmark.id)),
    ...current.filter((bookmark) => changed.has(bookmark.id)),
  ].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export const useBookmarkStore = create<BookmarkState>((set, get) => ({
  bookmarks: [],
  isLoading: false,

  loadBookmarks: async () => {
    await waitForAuthResolution();
    const readOnly = isCoreReadOnly();
    const ownerId = getAuthUserId();
    const authEpoch = getAuthEpoch();
    const loadKey = `${ownerId ?? 'signed-out'}\n${authEpoch}\n${readOnly ? 'read-only' : 'writable'}`;
    if (bookmarkLoadInFlight?.key === loadKey) {
      recordContentHydrationJoinedLoad('bookmarks');
      return bookmarkLoadInFlight.promise;
    }
    const requestId = ++bookmarkLoadRequestId;
    const startedAtMutationVersion = bookmarkMutationVersion;
    set({ isLoading: true });
    const promise = (async () => {
      try {
      const binding = ownerId ? await getContentOwnerBinding() : null;
      const authorizedOwner = Boolean(ownerId && binding === ownerId);
      let all: BibleBookmark[];
      if (!authorizedOwner) {
        // Signed-out/backend-free and owner-mismatch contexts never touch an
        // authored object store. Publishing empty also clears a prior owner's
        // in-memory projection without guessing ownership for legacy rows.
        all = [];
      } else if (readOnly) {
        all = await db.getAllBookmarks();
      } else {
        const [entities, legacy, initialized] = await Promise.all([
          getOwnerContentEntities(ownerId as string),
          db.getAllBookmarks(),
          hasContentLifecycleEntityBootstrap(ownerId as string, 'bookmark'),
        ]);
        const lifecycle = entities
          .filter((entity) => entity.entityType === 'bookmark' && entity.visibleDocument !== null)
          .map((entity) =>
            bookmarkFromCompleteDocument(entity.visibleDocument as CompleteBookmarkDocument),
          );
        all = !initialized && lifecycle.length === 0 ? legacy : lifecycle;
      }
      const live = all
        .filter((b) => !b.deletedAt)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
      await runContentHydrationPublishProbe('bookmarks');
      const finalBinding = ownerId ? await getContentOwnerBinding() : null;
      const finalAuthorizedOwner = Boolean(ownerId && finalBinding === ownerId);
      const canPublish =
        requestId === bookmarkLoadRequestId &&
        get().isLoading &&
        isCoreReadOnly() === readOnly &&
        getAuthUserId() === ownerId &&
        getAuthEpoch() === authEpoch &&
        finalAuthorizedOwner === authorizedOwner;
      if (canPublish) {
        set({
          bookmarks: authorizedOwner
            ? mergeBookmarkMutations(live, get().bookmarks, startedAtMutationVersion)
            : [],
        });
      } else {
        throw new ContentHydrationStaleError('owner-auth-or-request-changed');
      }
      } finally {
        if (requestId === bookmarkLoadRequestId) set({ isLoading: false });
      }
    })();
    bookmarkLoadInFlight = { key: loadKey, promise };
    try {
      await promise;
    } finally {
      if (bookmarkLoadInFlight?.promise === promise) bookmarkLoadInFlight = null;
    }
  },

  addBookmark: async (bookmark, recoveredAudio = []) => {
    assertCoreWriteAllowed('store:bookmark:add');
    if (!(await queueBookmarkMutation(bookmark, recoveredAudio))) {
      throw new Error('bookmark-durable-commit-rejected');
    }
    markBookmarkMutation(bookmark.id);
    set({ bookmarks: [bookmark, ...get().bookmarks] });
    requestBookmarkLifecycleSync();
  },

  updateBookmark: async (id, note, expectedUpdatedAt, recoveredAudio = []) => {
    assertCoreWriteAllowed('store:bookmark:update');
    const current = get().bookmarks.find((b) => b.id === id);
    if (!current) throw new BookmarkNotFoundError();
    const next = await queueBookmarkNoteMutation(
      current,
      note,
      new Date().toISOString(),
      expectedUpdatedAt,
      recoveredAudio,
    );
    markBookmarkMutation(id);
    set({ bookmarks: get().bookmarks.map((b) => (b.id === id ? next : b)) });
    requestBookmarkLifecycleSync();
  },

  removeBookmark: async (id) => {
    assertCoreWriteAllowed('store:bookmark:remove');
    // Read the full row from IDB (the store holds live rows; this is robust
    // even if called for a row not currently in memory).
    const current = (await db.getBookmark(id)) ?? get().bookmarks.find((b) => b.id === id);
    if (!current) throw new BookmarkNotFoundError();
    const now = new Date().toISOString();
    const tombstone: BibleBookmark = {
      ...current,
      deletedAt: now,
      updatedAt: now,
      // Force a push of the tombstone through the outbox so the delete
      // converges even if the user is offline / signed out right now.
      syncedAt: null,
    };
    // Keep the tombstone row in IDB (NOT a hard delete) so the outbox can
    // carry the deletion to the cloud; drop it from the visible list.
    if (!(await queueBookmarkMutation(tombstone))) {
      throw new Error('bookmark-durable-commit-rejected');
    }
    markBookmarkMutation(id);
    set({ bookmarks: get().bookmarks.filter((b) => b.id !== id) });
    requestBookmarkLifecycleSync();
  },
}));
